import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MailboxIcon, ParkingSquare, Trash2, KeyRound, Pencil, History, Car } from "lucide-react";

export default function ParkingPage() {
  const { propertyId } = useProperty();
  const [parking, setParking] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [residents, setResidents] = useState([]);
  // Parking dialog state
  const [pkOpen, setPkOpen] = useState(false);
  const [pkEditing, setPkEditing] = useState(null);
  const [pkForm, setPkForm] = useState({ spot: "", unit: "", resident_name: "", vehicle: "", plate: "", status: "active" });
  // Mailbox dialog state
  const [mbOpen, setMbOpen] = useState(false);
  const [mbEditing, setMbEditing] = useState(null);
  const [mbForm, setMbForm] = useState({ box_number: "", resident_id: "", unit: "", key_count: 2, notes: "" });
  const [keyLogOpen, setKeyLogOpen] = useState(false);
  const [keyLogBox, setKeyLogBox] = useState(null);
  const [keyLog, setKeyLog] = useState([]);
  const [keyEvent, setKeyEvent] = useState("checkout");
  const [keyNotes, setKeyNotes] = useState("");
  const [logging, setLogging] = useState(false);

  const loadParking = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/parking?property_id=${propertyId}`);
      setParking(data.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/ParkingPage.jsx: swallowed', err); }
  }, [propertyId]);

  const loadMailboxes = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/mailboxes?property_id=${propertyId}`);
      setMailboxes(data.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/ParkingPage.jsx: swallowed', err); }
  }, [propertyId]);

  const loadResidents = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/residents?property_id=${propertyId}`);
      setResidents(data.items || []);
    } catch { setResidents([]); }
  }, [propertyId]);

  useEffect(() => { loadParking(); loadMailboxes(); loadResidents(); }, [loadParking, loadMailboxes, loadResidents]);

  // --------- Parking CRUD ----------
  const openNewParking = () => {
    setPkEditing(null);
    setPkForm({ spot: "", unit: "", resident_name: "", vehicle: "", plate: "", status: "active" });
    setPkOpen(true);
  };
  const openEditParking = (p) => {
    setPkEditing(p);
    setPkForm({
      spot: p.spot || "", unit: p.unit || "", resident_name: p.resident_name || "",
      vehicle: p.vehicle || "", plate: p.plate || "", status: p.status || "active",
    });
    setPkOpen(true);
  };
  const savePk = async () => {
    if (!pkForm.spot.trim() || !pkForm.unit.trim() || !pkForm.resident_name.trim()) {
      return toast.error("Spot, unit, and resident name are required");
    }
    const body = {
      spot: pkForm.spot.trim(), unit: pkForm.unit.trim(),
      resident_name: pkForm.resident_name.trim(),
      vehicle: pkForm.vehicle.trim(), plate: pkForm.plate.trim(),
    };
    try {
      if (pkEditing) {
        await api.patch(`/parking/${pkEditing.id}`, { ...body, status: pkForm.status });
        toast.success("Parking updated");
      } else {
        await api.post(`/parking?property_id=${propertyId}`, body);
        toast.success("Parking assigned");
      }
      setPkOpen(false);
      loadParking();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const removePk = async (p) => {
    if (!window.confirm(`Delete parking spot ${p.spot}?`)) return;
    try { await api.delete(`/parking/${p.id}`); toast.success("Deleted"); loadParking(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const openNewMailbox = () => {
    setMbEditing(null);
    setMbForm({ box_number: "", resident_id: "", unit: "", key_count: 2, notes: "" });
    setMbOpen(true);
  };
  const openEditMailbox = (m) => {
    setMbEditing(m);
    setMbForm({
      box_number: m.box_number, resident_id: m.resident_id || "", unit: m.unit || "",
      key_count: m.key_count ?? 2, notes: m.notes || "",
    });
    setMbOpen(true);
  };

  const saveMailbox = async () => {
    if (!mbForm.box_number.trim()) return toast.error("Box number required");
    const payload = {
      box_number: mbForm.box_number.trim(),
      resident_id: mbForm.resident_id || null,
      unit: mbForm.unit || null,
      key_count: Number(mbForm.key_count) || 0,
      notes: mbForm.notes || null,
    };
    try {
      if (mbEditing) {
        await api.patch(`/admin/mailboxes/${mbEditing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/admin/mailboxes?property_id=${propertyId}`, payload);
        toast.success("Mailbox added");
      }
      setMbOpen(false);
      loadMailboxes();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const removeMailbox = async (m) => {
    if (!window.confirm(`Delete mailbox ${m.box_number}?`)) return;
    try {
      await api.delete(`/admin/mailboxes/${m.id}`);
      toast.success("Deleted");
      loadMailboxes();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const openKeyLog = async (m) => {
    setKeyLogBox(m);
    setKeyEvent("checkout");
    setKeyNotes("");
    setKeyLog([]);
    setKeyLogOpen(true);
    try {
      const { data } = await api.get(`/admin/mailboxes/${m.id}/key-events`);
      setKeyLog(data?.items || []);
    } catch { setKeyLog([]); }
  };

  const submitKeyEvent = async () => {
    if (!keyLogBox) return;
    setLogging(true);
    try {
      const { data } = await api.post(
        `/admin/mailboxes/${keyLogBox.id}/key-event`,
        { event: keyEvent,
          resident_id: keyLogBox.resident_id || null,
          notes: keyNotes.trim() || null }
      );
      toast.success(`${keyEvent} logged · keys out: ${data.keys_out}`);
      setKeyNotes("");
      const evs = await api.get(`/admin/mailboxes/${keyLogBox.id}/key-events`);
      setKeyLog(evs.data?.items || []);
      loadMailboxes();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLogging(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Facilities"
        title="Parking & Mailboxes"
        subtitle="Every stall, vehicle, and mailbox in one place."
      />

      <Tabs defaultValue="parking">
        <TabsList className="bg-white/[0.03] border border-white/10">
          <TabsTrigger value="parking" data-testid="tab-parking">
            <ParkingSquare className="h-3.5 w-3.5 mr-1" /> Parking ({parking.length})
          </TabsTrigger>
          <TabsTrigger value="mailboxes" data-testid="tab-mailboxes">
            <MailboxIcon className="h-3.5 w-3.5 mr-1" /> Mailboxes ({mailboxes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="parking" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-full" onClick={openNewParking} data-testid="new-parking-btn">
              <Plus className="h-4 w-4 mr-1" /> Assign spot
            </Button>
          </div>
          <Card className="border-white/10 bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead>Spot</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Resident</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Plate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parking.map((p) => (
                  <TableRow key={p.id} className="border-white/5" data-testid={`park-${p.id}`}>
                    <TableCell className="font-mono">{p.spot}</TableCell>
                    <TableCell>{p.unit}</TableCell>
                    <TableCell>{p.resident_name}</TableCell>
                    <TableCell>{p.vehicle}</TableCell>
                    <TableCell className="font-mono text-xs">{p.plate}</TableCell>
                    <TableCell><Badge variant="outline" className="border-white/10 capitalize">{p.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditParking(p)} data-testid={`park-edit-${p.id}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => removePk(p)} data-testid={`park-delete-${p.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {parking.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm">
                    <Car className="h-6 w-6 mx-auto mb-2 opacity-60" />
                    No parking assignments yet — assign a spot to a resident.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="mailboxes" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-full" onClick={openNewMailbox} data-testid="new-mailbox-btn">
              <Plus className="h-4 w-4 mr-1" /> New mailbox
            </Button>
          </div>
          <Card className="border-white/10 bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead>Box #</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Resident</TableHead>
                  <TableHead>Keys</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mailboxes.map((m) => (
                  <TableRow key={m.id} className="border-white/5" data-testid={`mb-${m.id}`}>
                    <TableCell className="font-mono">{m.box_number}</TableCell>
                    <TableCell>{m.unit || "—"}</TableCell>
                    <TableCell>{m.resident_name || <span className="text-zinc-500 italic">Unassigned</span>}</TableCell>
                    <TableCell className="tabular-nums flex items-center gap-1"><KeyRound className="h-3 w-3 text-zinc-500" /> {m.key_count ?? "—"}</TableCell>
                    <TableCell className="text-xs text-zinc-400 max-w-xs truncate">{m.notes || ""}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openKeyLog(m)} data-testid={`mb-keylog-${m.id}`} title="Key log">
                          <History className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditMailbox(m)} data-testid={`mb-edit-${m.id}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => removeMailbox(m)} data-testid={`mb-delete-${m.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {mailboxes.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No mailboxes on file yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={pkOpen} onOpenChange={setPkOpen}>
        <DialogContent className="bg-card border-white/10" data-testid="pk-dialog">
          <DialogHeader><DialogTitle>{pkEditing ? "Edit parking assignment" : "Assign parking spot"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Spot #</Label><Input data-testid="pk-form-spot" value={pkForm.spot} onChange={(e) => setPkForm({ ...pkForm, spot: e.target.value })} placeholder="e.g. G-27" /></div>
              <div><Label>Unit</Label><Input data-testid="pk-form-unit" value={pkForm.unit} onChange={(e) => setPkForm({ ...pkForm, unit: e.target.value })} placeholder="12B" /></div>
              <div className="col-span-2"><Label>Resident name</Label><Input data-testid="pk-form-resident" value={pkForm.resident_name} onChange={(e) => setPkForm({ ...pkForm, resident_name: e.target.value })} placeholder="Jane Smith" /></div>
              <div><Label>Vehicle</Label><Input data-testid="pk-form-vehicle" value={pkForm.vehicle} onChange={(e) => setPkForm({ ...pkForm, vehicle: e.target.value })} placeholder="2023 Tesla Model 3" /></div>
              <div><Label>Plate</Label><Input data-testid="pk-form-plate" value={pkForm.plate} onChange={(e) => setPkForm({ ...pkForm, plate: e.target.value })} placeholder="ABC-1234" /></div>
              {pkEditing && (
                <div className="col-span-2">
                  <Label>Status</Label>
                  <Select value={pkForm.status} onValueChange={(v) => setPkForm({ ...pkForm, status: v })}>
                    <SelectTrigger data-testid="pk-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="ev_charger">EV charger</SelectItem>
                      <SelectItem value="guest">Guest</SelectItem>
                      <SelectItem value="reserved">Reserved</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <Button data-testid="pk-form-save" className="w-full bg-orange-500 hover:bg-orange-600" onClick={savePk}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={mbOpen} onOpenChange={setMbOpen}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader><DialogTitle>{mbEditing ? "Edit mailbox" : "New mailbox"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Box #</Label><Input data-testid="mb-box" value={mbForm.box_number} onChange={(e) => setMbForm({ ...mbForm, box_number: e.target.value })} placeholder="e.g. B-14" /></div>
              <div><Label>Unit (optional)</Label><Input data-testid="mb-unit" value={mbForm.unit} onChange={(e) => setMbForm({ ...mbForm, unit: e.target.value })} placeholder="e.g. 12B" /></div>
            </div>
            <div>
              <Label>Assign to resident</Label>
              <Select value={mbForm.resident_id || "none"} onValueChange={(v) => setMbForm({ ...mbForm, resident_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="mb-resident" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} — Unit {r.unit || "—"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Keys issued</Label><Input data-testid="mb-keys" type="number" min="0" max="10" value={mbForm.key_count} onChange={(e) => setMbForm({ ...mbForm, key_count: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea data-testid="mb-notes" rows={2} value={mbForm.notes} onChange={(e) => setMbForm({ ...mbForm, notes: e.target.value })} placeholder="Broken lock replaced 2026-01…" /></div>
            <Button data-testid="mb-save" className="w-full bg-orange-500 hover:bg-orange-600" onClick={saveMailbox}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={keyLogOpen} onOpenChange={setKeyLogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="key-log-dialog">
          <DialogHeader>
            <DialogTitle>Key log · Box {keyLogBox?.box_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label>Event</Label>
                <Select value={keyEvent} onValueChange={setKeyEvent}>
                  <SelectTrigger data-testid="key-event-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checkout">Checkout (key out)</SelectItem>
                    <SelectItem value="return">Return (key back)</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                    <SelectItem value="replace">Replace (rekey)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assignee</Label>
                <Input value={keyLogBox?.resident_name || "Unassigned"} readOnly
                       className="text-zinc-400" data-testid="key-event-assignee" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={keyNotes} onChange={(e) => setKeyNotes(e.target.value)}
                        placeholder="Optional context — who received the key, why replaced, etc."
                        data-testid="key-event-notes" />
            </div>
            <Button data-testid="key-event-submit" onClick={submitKeyEvent}
                    disabled={logging}
                    className="w-full bg-orange-500 hover:bg-orange-600">
              {logging ? "Logging…" : "Log event"}
            </Button>
            <div className="pt-3 border-t border-white/10">
              <div className="text-xs text-zinc-500 mb-2">History ({keyLog.length})</div>
              {keyLog.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">No key events yet.</div>
              ) : (
                <div className="space-y-1 max-h-60 overflow-y-auto" data-testid="key-log-history">
                  {keyLog.map((e) => (
                    <div key={e.id} className="text-xs flex items-center gap-2 border-b border-white/5 pb-1">
                      <KeyRound className="h-3 w-3 text-orange-300 shrink-0" />
                      <span className="font-medium text-zinc-300 capitalize">{e.event}</span>
                      <span className="text-zinc-500">·</span>
                      <span className="text-zinc-400">{e.resident_name || "—"}</span>
                      <span className="text-zinc-500 ml-auto">
                        {new Date(e.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </span>
                      {e.notes && <div className="basis-full text-zinc-500 ml-5">{e.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
